/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
/** biome-ignore-all lint/suspicious/noArrayIndexKey: use better keys */
import {useCallback, useEffect, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import {ConfirmRemoveSectionModal} from '~/CreateFabricInvitation/routes/EventDetails/components/ConfirmRemoveSectionModal';
import {WtbItems} from '~/common/fabric/CreateDesign/components/WhatToBring/WhatToBringItems';
import {CREATE_EDIT_WTB} from '~/common/fabric/reducers/widgets/constants';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';
import {PlusIcon} from '~library/svg/v2/PlusIcon';
import {scrollElementIntoViewById} from '~library/utils/misc';

import {tanzanite} from '~libSass/v2/_exports.sass';
import './WhatToBringOverlay.sass';

const defaultWhatToBring = {
  items: [
    {name: '', placeholder: 'ex: Chips', quantity: 1, autofocus: true},
    {name: '', placeholder: 'ex: Salsa', quantity: 1},
    {name: '', placeholder: 'ex: Drinks', quantity: 1},
  ],
  editing: false,
  allow_other_items: false,
  formWasEmptyOnSubmit: false,
  emptyRows: [],
};

const scrollIntoView = () => scrollElementIntoViewById('module-what_to_bring');

export const WtbOverlay = ({isOpen, onClose, overlayCallback, title, body, hasWtb}) => {
  const minItems = 1;
  // TODO update with actual max count
  const maxItems = 50;
  const store = useStore();
  const eventWidgets = useSelector(({widgets}) => widgets);
  const [validationErrors, setValidationErrors] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [itemValues, setItemValues] = useState([]);
  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState(false);
  const canRemove = itemValues.length > minItems;
  const showAddItemLink = itemValues.length < maxItems;
  const allowOther = hasWtb ? eventWidgets?.whatToBrings[0]?.allow_other_items : false;
  const wtbId = hasWtb ? eventWidgets?.whatToBrings[0]?.id : undefined;

  const completeItems = itemValues.filter((o) => o.name !== '' && o.quantity && o.quantity >= 1);
  const [validItemsToSubmit, setValidItemsToSubmit] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setItemValues(
      structuredClone(hasWtb ? eventWidgets?.whatToBrings[0]?.items : defaultWhatToBring.items)
    );
  }, [isOpen, hasWtb, eventWidgets?.whatToBrings]);

  const hasDuplicateItems = (items) => {
    // Get a flat array of item names & ignore casing differences
    const optNameList = items.map((opt) => opt.name.toLowerCase().trim());
    // Check if at-least one of the items passes the check. Returns true/false
    return optNameList.some(
      (item, i) =>
        // If the found index is not the same as the current item's index, it's a duplicate
        optNameList.indexOf(item) !== i
    );
  };

  const validateFields = () => {
    if (hasDuplicateItems(completeItems)) {
      setValidationErrors('Two or more of your items are the same. Please update and try again.');
    } else if (!validationErrors) {
      setValidItemsToSubmit(completeItems);
    } else {
      setValidationErrors('Sorry, something went wrong, please try again.');
    }
  };

  const shouldDisableButton = () => {
    validateFields();
    return Boolean(completeItems.length === 0 || validationErrors);
  };

  const handleSaveWtb = useCallback(() => {
    const wtbWidget = {
      allow_other_items: allowOther,
      id: wtbId,
      items: validItemsToSubmit,
    };
    store.dispatch({
      type: CREATE_EDIT_WTB,
      payload: {whatToBrings: [wtbWidget]},
    });
    overlayCallback(wtbWidget);
    onClose(!!wtbWidget);
    scrollIntoView();
  }, [allowOther, onClose, overlayCallback, store, validItemsToSubmit, wtbId]);

  const handleConfirmRemoveWtb = useCallback(() => {
    scrollIntoView();
    store.dispatch({
      type: CREATE_EDIT_WTB,
      payload: {whatToBrings: []},
    });
    overlayCallback(null);
    onClose(false);
    setItemValues(structuredClone(defaultWhatToBring.items));
  }, [onClose, overlayCallback, store]);

  const handleRemoveWtb = useCallback(() => {
    setIsConfirmRemoveModalOpen(true);
  }, []);

  const handleCloseConfirmRemoveModal = useCallback(() => {
    setIsConfirmRemoveModalOpen(false);
  }, []);

  const handleAddItem = () => {
    const newArray = [
      ...itemValues,
      {name: '', placeholder: 'ex: Cups', quantity: 1, autofocus: true},
    ];
    setItemValues(newArray);
  };

  const itemCallback = (index, e, type) => {
    if (type === 'remove') {
      const filtered = itemValues.filter((o, i) => i !== index);
      setValidationErrors(null);
      setItemValues(filtered);
    } else {
      setValidationErrors(null);
      const newValues = [...itemValues];

      if (type === 'nameChange') {
        newValues[index] = {...newValues[index], name: e.target.value};
      } else if (type === 'qtyChange') {
        if (parseInt(e.target.value, 10) < 1 || e.target.value === '') {
          newValues[index] = {
            ...newValues[index],
            quantity: '',
          };
        } else {
          newValues[index] = {
            ...newValues[index],
            quantity: parseInt(e.target.value, 10),
          };
        }
      }
      setItemValues(newValues);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    setValidationErrors(null);
  }, [isOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setButtonDisabled(shouldDisableButton());
  }, [itemValues, validationErrors]);

  return (
    <ResponsivePanel
      isOpen={isOpen}
      onClose={() => onClose(hasWtb)}
      title={title}
      body={body}
      showCover
      modalId="what-to-bring"
      renderMobileViewAs="bottom-sheet"
      portalMode
      disableOverlayClick
    >
      <div className="wtb__overlay">
        <Typography variant="paragraph2" className="wtb__overlay-copy">
          {body}
        </Typography>

        <div className="wtb__items">
          <div className="wtb__items__labels">
            <Typography variant="label2">ITEMS</Typography>
            <Typography variant="label2" className="wtb__items__label--centered">
              QTY
            </Typography>
          </div>
          {itemValues.map((opt, i) => (
            <WtbItems
              key={`item-${i}`}
              itemCallback={itemCallback}
              opt={opt}
              itemNum={i + 1}
              index={i}
              canRemove={canRemove}
              itemValues={itemValues}
              setItemValues={setItemValues}
            />
          ))}
          {showAddItemLink && (
            <Button
              onClick={handleAddItem}
              variant="link"
              data-qa-id="button_add-wtb-item"
              type="button"
              className="wtb__add-button"
            >
              <PlusIcon ratio={0.5} color={tanzanite} />
              Add another item
            </Button>
          )}
        </div>

        {validationErrors && (
          <Typography
            className="input-error"
            role="alert"
            aria-live="assertive"
            variant="paragraph3"
            style={{textAlign: 'left'}}
          >
            {validationErrors}
          </Typography>
        )}
      </div>

      <div className="wtb-overlay__ctas">
        <Button
          className="wtb__items__save-button"
          onClick={handleSaveWtb}
          data-qa-id="editing-wtb"
          variant="primary"
          size="large"
          fullWidth
          disabled={buttonDisabled}
          type="button"
        >
          Save
        </Button>
        {hasWtb && (
          <Button
            onClick={handleRemoveWtb}
            data-qa-id="remove-wtb"
            variant="transparent"
            className="wtb-overlay__remove-button"
            size="large"
            type="button"
          >
            Remove section
          </Button>
        )}
        {isConfirmRemoveModalOpen && (
          <ConfirmRemoveSectionModal
            isOpen
            onClose={handleCloseConfirmRemoveModal}
            onConfirm={handleConfirmRemoveWtb}
            qaId="confirm-remove-wtb"
          />
        )}
      </div>
    </ResponsivePanel>
  );
};
